import { ChevronUp, ChevronDown, CreditCard, Receipt, Copy } from 'lucide-react';
import { fmtMoney, fmtNumber, fmtDate } from './format';
import { btn, card, tbl } from '../../theme';

const COLUMNS = [
  { key: 'line_date',   label: 'Date',        sort: 'line_date',  width: 92 },
  { key: 'employee',    label: 'Employee',    sort: 'employee',   width: 150 },
  { key: 'cost_center', label: 'Cost Center', sort: 'cost_center', width: 150 },
  { key: 'item',        label: 'Expense Item', sort: 'item',      width: 150 },
  { key: 'category',    label: 'Spend Category', sort: 'category', width: 150 },
  { key: 'merchant',    label: 'Merchant',    sort: 'merchant',   width: 160 },
  { key: 'memo',        label: 'Memo',        sort: null,         width: 200 },
  { key: 'amount',      label: 'Amount',      sort: 'amount',     width: 100, right: true },
];

export default function TransactionTable({ data, loading, sort, dir, onSort, page, onPage }) {
  const rows   = data?.transactions || [];
  const totals = data?.totals;
  const pageCount = data?.page_count || 1;

  return (
    <div style={{ ...s.card, opacity: loading ? 0.6 : 1 }}>
      <div style={s.header}>
        <div>
          <div style={s.title}>Transactions</div>
          <div style={s.subtitle}>
            {totals ? `${fmtNumber(totals.transaction_count)} matching · showing page ${page} of ${fmtNumber(pageCount)}` : ' '}
          </div>
        </div>
      </div>

      <div style={s.scroll}>
        <table style={s.table}>
          <thead>
            <tr>
              {COLUMNS.map((c) => (
                <th key={c.key} style={{ ...s.th, width: c.width, textAlign: c.right ? 'right' : 'left',
                                          cursor: c.sort ? 'pointer' : 'default' }}
                    onClick={() => c.sort && onSort(c.sort)}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3,
                                 justifyContent: c.right ? 'flex-end' : 'flex-start' }}>
                    {c.label}
                    {sort === c.sort && (dir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={COLUMNS.length} style={s.empty}>No transactions match these filters.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} style={s.tr}>
                <td style={s.td}>{fmtDate(r.line_date)}</td>
                <td style={s.td}>
                  <div style={s.emp}>{r.employee_name || '—'}</div>
                  {r.supervisors && <div style={s.sub}>{r.supervisors}</div>}
                </td>
                <td style={s.td}>
                  {r.cost_center_code
                    ? <span>{r.cost_center_code} {r.cost_center_name}</span>
                    : <span style={s.muted}>{r.raw_cost_center || '—'}</span>}
                  {r.line_of_business && <div style={s.sub}>{r.line_of_business}</div>}
                </td>
                <td style={s.td}>{r.expense_item}</td>
                <td style={s.td}>
                  <span style={r.spend_category === '<Unmapped>' ? s.unmapped : undefined}>{r.spend_category}</span>
                </td>
                <td style={s.td}>
                  {r.is_reimbursement ? (
                    <span style={s.reimbTag}><Receipt size={10} /> Reimbursement</span>
                  ) : (
                    <>
                      <span style={s.cardTag}><CreditCard size={10} /> {r.merchant_canonical || '—'}</span>
                      {r.merchant_raw && r.merchant_raw !== r.merchant_canonical && (
                        <div style={s.sub} title={r.merchant_raw}>{r.merchant_raw}</div>
                      )}
                    </>
                  )}
                </td>
                <td style={{ ...s.td, ...s.memo }} title={r.memo || ''}>{r.memo || '—'}</td>
                <td style={{ ...s.td, textAlign: 'right', fontWeight: 600,
                             color: r.line_amount < 0 ? '#B45309' : '#111827',
                             fontVariantNumeric: 'tabular-nums' }}>
                  {fmtMoney(r.line_amount)}
                  {/* occurrence_index > 1 means an identical row arrived in the
                      same file. Kept on purpose — flagged, never merged. */}
                  {r.occurrence_index > 1 && (
                    <span style={s.repeatTag} title="An identical row appeared in the same import. Kept — see Review Flags.">
                      <Copy size={9} /> {r.occurrence_index}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <div style={s.pager}>
          <button style={s.pageBtn} disabled={page <= 1} onClick={() => onPage(page - 1)}>← Prev</button>
          <span style={s.pageInfo}>Page {fmtNumber(page)} of {fmtNumber(pageCount)}</span>
          <button style={s.pageBtn} disabled={page >= pageCount} onClick={() => onPage(page + 1)}>Next →</button>
        </div>
      )}
    </div>
  );
}

const s = {
  card:     { ...card.sm, padding: '14px 0 0', transition: 'opacity 0.15s' },
  header:   { padding: '0 16px 10px' },
  title:    { fontSize: '13px', fontWeight: 600, color: '#111827' },
  subtitle: { fontSize: '11px', color: '#9CA3AF', marginTop: '2px' },
  scroll:   { overflowX: 'auto' },
  table:    { width: '100%', borderCollapse: 'collapse', fontSize: '11.5px' },
  th: {
    padding: '8px 10px', background: '#F9FAFB', borderTop: '1px solid #E5E7EB',
    borderBottom: '1px solid #E5E7EB', fontSize: '10px', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6B7280', whiteSpace: 'nowrap',
  },
  tr:  { ...tbl.tr, ...tbl.tr },
  td:  { ...tbl.td, ...tbl.tdSm, verticalAlign: 'top' },
  emp: { fontWeight: 500, color: '#111827' },
  sub: { fontSize: '10px', color: '#9CA3AF', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '160px' },
  muted:{ color: '#9CA3AF' },
  memo: { maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#6B7280' },
  unmapped: { background: '#FEF3C7', color: '#92400E', padding: '1px 5px', borderRadius: '4px', fontSize: '10px' },
  cardTag:  { display: 'inline-flex', alignItems: 'center', gap: '3px' },
  reimbTag: { display: 'inline-flex', alignItems: 'center', gap: '3px', color: '#6B7280', fontSize: '11px' },
  repeatTag:{ display: 'inline-flex', alignItems: 'center', gap: '2px', marginLeft: '5px', padding: '0 4px',
              background: '#FEF3C7', color: '#92400E', borderRadius: '3px', fontSize: '9px', fontWeight: 600 },
  empty:  { padding: '36px', textAlign: 'center', color: '#9CA3AF', fontSize: '12px' },
  pager:  { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px', padding: '10px' },
  pageBtn:{ ...btn.secondaryXs },
  pageInfo:{ fontSize: '11px', color: '#6B7280' },
};
